package br.com.vvrefeicoes.portal.feature.companies

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.util.DateTimeProvider
import br.com.vvrefeicoes.portal.core.util.PercentFormatter
import br.com.vvrefeicoes.portal.core.util.SystemDateTimeProvider
import br.com.vvrefeicoes.portal.domain.model.Company
import br.com.vvrefeicoes.portal.domain.model.CompanyEmotion
import br.com.vvrefeicoes.portal.domain.model.CompanyMissingSyncResult
import br.com.vvrefeicoes.portal.domain.model.CompanyService
import br.com.vvrefeicoes.portal.domain.model.CompanyServiceDraft
import br.com.vvrefeicoes.portal.domain.model.CompanyType
import br.com.vvrefeicoes.portal.domain.model.CostCenter
import br.com.vvrefeicoes.portal.domain.model.DEFAULT_ADMIN_OTHERS_PERCENT
import br.com.vvrefeicoes.portal.domain.model.Line
import br.com.vvrefeicoes.portal.domain.model.ServiceTypeOption
import br.com.vvrefeicoes.portal.domain.repository.CepRepository
import br.com.vvrefeicoes.portal.domain.repository.CompanyRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

enum class CompanyFormMode { Add, Edit }

private fun emotionOrder(name: String): Int =
    when (name.trim().lowercase()) {
        "ótimo" -> 0
        "bom" -> 1
        "regular" -> 2
        "ruim" -> 3
        else -> 4
    }

class CompaniesController(
    private val repository: CompanyRepository,
    private val scope: CoroutineScope,
    private val dateTimeProvider: DateTimeProvider = SystemDateTimeProvider(),
    private val cepRepository: CepRepository = EmptyCepRepository,
) {
    private var initialized = false
    private var initializing = false

    var loading by mutableStateOf(true)
        private set

    var busy by mutableStateOf(false)
        private set

    var errorMessage by mutableStateOf<String?>(null)
        private set

    var syncingFromSatisfaction by mutableStateOf(false)
        private set

    var lastMissingSync by mutableStateOf<CompanyMissingSyncResult?>(null)
        private set

    var companies by mutableStateOf<List<Company>>(emptyList())
        private set

    var costCenters by mutableStateOf<List<CostCenter>>(emptyList())
        private set

    var costCenterBusy by mutableStateOf(false)
        private set

    var costCenterDialog by mutableStateOf<CostCenter?>(null)
        private set

    var costCenterCompanyId by mutableStateOf<String?>(null)
        private set

    var costCenterName by mutableStateOf("")
    var costCenterCode by mutableStateOf("")

    var servicesByCompany by mutableStateOf<Map<String, List<CompanyService>>>(emptyMap())
        private set

    var expandedCompanyId by mutableStateOf<String?>(null)
        private set

    var loadingServicesFor by mutableStateOf<String?>(null)
        private set

    var serviceDialogCompanyId by mutableStateOf<String?>(null)
        private set

    var serviceDialogService by mutableStateOf<CompanyService?>(null)
        private set

    var serviceTypes by mutableStateOf<List<ServiceTypeOption>>(emptyList())
        private set

    var lines by mutableStateOf<List<Line>>(emptyList())
        private set

    var lineDialogOpen by mutableStateOf(false)
        private set

    var lineCatalogOpen by mutableStateOf(false)
        private set

    var lineDialogLine by mutableStateOf<Line?>(null)
        private set

    var lineFormCode by mutableStateOf("")
    var lineFormName by mutableStateOf("")
    var lineFormDisplayOrder by mutableStateOf("0")
    var lineFormDefault by mutableStateOf(false)
    var lineBusy by mutableStateOf(false)

    var serviceBusy by mutableStateOf(false)
        private set

    var emotionCompanyId by mutableStateOf<String?>(null)
        private set
    var emotionCatalog by mutableStateOf<List<CompanyEmotion>>(emptyList())
        private set
    var selectedEmotionIds by mutableStateOf<Set<String>>(emptySet())
        private set
    var selectedEmotions by mutableStateOf<List<CompanyEmotion>>(emptyList())
        private set
    var emotionResponseDrafts by mutableStateOf<Map<String, String>>(emptyMap())
        private set
    var emotionLoading by mutableStateOf(false)
        private set
    var emotionBusy by mutableStateOf(false)
        private set
    var editingEmotionResponseId by mutableStateOf<String?>(null)
        private set
    var editingEmotionResponseText by mutableStateOf("")

    var serviceDeleteTarget by mutableStateOf<Pair<String, String>?>(null)
        private set

    var query by mutableStateOf("")
        private set

    var onlyActive by mutableStateOf(true)
        private set

    var page by mutableStateOf(0)
        private set

    val pageSize = 20

    var formMode by mutableStateOf<CompanyFormMode?>(null)

    var formStep by mutableStateOf(0)
        private set

    var formId by mutableStateOf<String?>(null)

    var formName by mutableStateOf("")

    var formLegalName by mutableStateOf("")

    var formType by mutableStateOf(CompanyType.UPR)

    var formLogo by mutableStateOf("")
    var formEmail by mutableStateOf("")
    var formLogin by mutableStateOf("")
    var formPassword by mutableStateOf("")
    var formCommercialPhone by mutableStateOf("")
    var formMobilePhone by mutableStateOf("")
    var formCnpj by mutableStateOf("")
    var formEmployeeCount by mutableStateOf("")
    var formCep by mutableStateOf("")
    var formStreet by mutableStateOf("")
    var formNumber by mutableStateOf("")
    var formNeighborhood by mutableStateOf("")
    var formCity by mutableStateOf("")
    var formState by mutableStateOf("")
    var formLeftoverMeals by mutableStateOf("")
    var formLine by mutableStateOf("")
    var formButtonCount by mutableStateOf("")
    var formShortAutoSendTime by mutableStateOf("")
    var formLongAutoSendTime by mutableStateOf("")
    var formCommentAllRatings by mutableStateOf(false)

    var formAdminOthersPercent by mutableStateOf("")

    var cepLoading by mutableStateOf(false)
        private set

    private var lastLookedUpCep = ""

    var detailsCompany by mutableStateOf<Company?>(null)

    var deactivateTarget by mutableStateOf<Company?>(null)

    val directorOptions: List<String>
        get() = companies.flatMap { it.directorNames }.distinct().sorted()

    val managerOptions: List<String>
        get() = companies.flatMap { it.managerNames }.distinct().sorted()

    val filtered: List<Company>
        get() {
            val text = query.trim()
            return companies.filter { company ->
                (!onlyActive || !company.inactive) &&
                    (
                        text.isEmpty() ||
                            company.name.contains(text, ignoreCase = true) ||
                            company.legalName.contains(text, ignoreCase = true) ||
                            company.directorNames.any { it.contains(text, ignoreCase = true) } ||
                            company.managerNames.any { it.contains(text, ignoreCase = true) }
                    )
            }
        }

    val pageCount: Int
        get() = maxOf(1, (filtered.size + pageSize - 1) / pageSize)

    val paged: List<Company>
        get() = filtered.drop(page * pageSize).take(pageSize)

    val isFormConfirmEnabled: Boolean
        get() = formName.isNotBlank() && formLegalName.isNotBlank() && !hasInvalidNumericFields && !hasInvalidAdminOthersPercent

    val hasInvalidAdminOthersPercent: Boolean
        get() = PercentFormatter.parse(formAdminOthersPercent) == null

    val hasInvalidNumericFields: Boolean
        get() =
            listOf(
                formEmployeeCount,
                formLeftoverMeals,
                formButtonCount,
                formShortAutoSendTime,
                formLongAutoSendTime,
            ).any { it.isNotBlank() && it.toIntOrNull() == null }

    val canAdvanceForm: Boolean
        get() =
            when (formStep) {
                0 -> formName.isNotBlank() && formLegalName.isNotBlank()
                1 -> formEmployeeCount.isBlank() || formEmployeeCount.toIntOrNull() != null
                else -> isFormConfirmEnabled
            }

    fun init() {
        if (initialized || initializing) return
        initializing = true
        scope.launch {
            loading = true
            try {
                refresh()
                initialized = true
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível carregar as unidades."
            } finally {
                loading = false
                initializing = false
            }
        }
    }

    fun retry() {
        errorMessage = null
        if (!initialized) {
            init()
            return
        }
        scope.launch {
            loading = true
            try {
                refresh()
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível carregar as unidades."
            } finally {
                loading = false
            }
        }
    }

    fun updateQuery(value: String) {
        query = value
        page = 0
    }

    fun updateOnlyActive(value: Boolean) {
        onlyActive = value
        page = 0
    }

    fun syncMissingFromSatisfaction() {
        if (syncingFromSatisfaction) return
        syncingFromSatisfaction = true
        scope.launch {
            try {
                lastMissingSync = repository.syncMissingFromSatisfaction()
                refresh()
                errorMessage = null
            } catch (exception: Exception) {
                errorMessage = exception.message ?: "Não foi possível importar os dados do Satisfaction."
            } finally {
                syncingFromSatisfaction = false
            }
        }
    }

    fun toggleServices(companyId: String) {
        if (expandedCompanyId == companyId) {
            expandedCompanyId = null
            return
        }
        expandedCompanyId = companyId
        if (servicesByCompany.containsKey(companyId)) return
        scope.launch {
            loadingServicesFor = companyId
            try {
                servicesByCompany = servicesByCompany + (companyId to repository.loadServices(companyId))
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível carregar os serviços."
            } finally {
                loadingServicesFor = null
            }
        }
    }

    fun openEmotions(companyId: String) {
        if (emotionLoading) return
        emotionCompanyId = companyId
        emotionLoading = true
        scope.launch {
            try {
                emotionCatalog = repository.loadEmotionCatalog(companyId).sortedBy { emotionOrder(it.name) }
                selectedEmotions = repository.loadCompanyEmotions(companyId)
                selectedEmotionIds = selectedEmotions.map { it.id }.toSet()
            } catch (exception: Exception) {
                errorMessage = exception.message ?: "Não foi possível carregar os emoticons."
                emotionCompanyId = null
            } finally {
                emotionLoading = false
            }
        }
    }

    fun toggleEmotion(
        id: String,
        selected: Boolean,
    ) {
        selectedEmotionIds = if (selected) selectedEmotionIds + id else selectedEmotionIds - id
    }

    fun saveEmotions() {
        val companyId = emotionCompanyId ?: return
        if (emotionBusy) return
        emotionBusy = true
        scope.launch {
            try {
                repository.updateCompanyEmotions(companyId, selectedEmotionIds.toList())
                emotionCompanyId = null
            } catch (exception: Exception) {
                errorMessage = exception.message ?: "Não foi possível salvar os emoticons."
            } finally {
                emotionBusy = false
            }
        }
    }

    fun updateEmotionResponseDraft(
        emotionId: String,
        text: String,
    ) {
        emotionResponseDrafts = emotionResponseDrafts + (emotionId to text)
    }

    fun addEmotionResponse(emotionId: String) {
        val companyId = emotionCompanyId ?: return
        val text = emotionResponseDrafts[emotionId]?.trim().orEmpty()
        if (text.isBlank() || emotionBusy) return
        emotionBusy = true
        scope.launch {
            try {
                repository.addEmotionResponse(companyId, emotionId, text)
                selectedEmotions = repository.loadCompanyEmotions(companyId)
                emotionResponseDrafts = emotionResponseDrafts - emotionId
            } catch (exception: Exception) {
                errorMessage = exception.message ?: "Não foi possível adicionar a resposta."
            } finally {
                emotionBusy = false
            }
        }
    }

    fun startEditEmotionResponse(
        id: String,
        text: String,
    ) {
        editingEmotionResponseId = id
        editingEmotionResponseText = text
    }

    fun cancelEditEmotionResponse() {
        editingEmotionResponseId = null
        editingEmotionResponseText = ""
    }

    fun saveEmotionResponse() {
        val companyId = emotionCompanyId ?: return
        val responseId = editingEmotionResponseId ?: return
        val text = editingEmotionResponseText.trim()
        if (text.isBlank() || emotionBusy) return
        emotionBusy = true
        scope.launch {
            try {
                repository.updateEmotionResponse(responseId, text)
                selectedEmotions = repository.loadCompanyEmotions(companyId)
                cancelEditEmotionResponse()
            } catch (exception: Exception) {
                errorMessage = exception.message ?: "Não foi possível editar a resposta."
            } finally {
                emotionBusy = false
            }
        }
    }

    fun deleteEmotionResponse(responseId: String) {
        val companyId = emotionCompanyId ?: return
        if (emotionBusy) return
        emotionBusy = true
        scope.launch {
            try {
                repository.deleteEmotionResponse(responseId)
                selectedEmotions = repository.loadCompanyEmotions(companyId)
            } catch (exception: Exception) {
                errorMessage = exception.message ?: "Não foi possível remover a resposta."
            } finally {
                emotionBusy = false
            }
        }
    }

    fun dismissEmotions() {
        if (!emotionBusy) emotionCompanyId = null
    }

    fun openAddService(companyId: String) {
        serviceDialogCompanyId = companyId
        serviceDialogService = null
        loadServiceOptions()
    }

    fun openEditService(
        companyId: String,
        service: CompanyService,
    ) {
        serviceDialogCompanyId = companyId
        serviceDialogService = service
        loadServiceOptions()
    }

    fun dismissServiceForm() {
        serviceDialogCompanyId = null
        serviceDialogService = null
    }

    fun openAddLine() {
        lineCatalogOpen = true
        lineDialogOpen = true
        lineDialogLine = null
        lineFormCode = ""
        lineFormName = ""
        lineFormDisplayOrder = lines.size.plus(1).toString()
        lineFormDefault = false
    }

    fun openEditLine(line: Line) {
        lineCatalogOpen = true
        lineDialogOpen = true
        lineDialogLine = line
        lineFormCode = line.code
        lineFormName = line.name
        lineFormDisplayOrder = line.displayOrder.toString()
        lineFormDefault = line.isDefault
    }

    fun dismissLineForm() {
        lineDialogOpen = false
        lineDialogLine = null
    }

    fun openLineCatalog() {
        lineCatalogOpen = true
    }

    fun dismissLineCatalog() {
        if (!lineDialogOpen) lineCatalogOpen = false
    }

    fun saveLine() {
        if (lineBusy || lineFormCode.isBlank() || lineFormName.isBlank()) return
        lineBusy = true
        scope.launch {
            try {
                val current = lineDialogLine
                if (current == null) {
                    repository.createLine(lineFormCode.trim(), lineFormName.trim(), lineFormDisplayOrder.toIntOrNull() ?: 0, lineFormDefault)
                } else {
                    repository.updateLine(
                        current.copy(
                            code = lineFormCode.trim(),
                            name = lineFormName.trim(),
                            displayOrder = lineFormDisplayOrder.toIntOrNull() ?: current.displayOrder,
                            isDefault = lineFormDefault,
                        ),
                    )
                }
                lines = repository.loadLines()
                dismissLineForm()
                errorMessage = null
            } catch (exception: Exception) {
                errorMessage = exception.message ?: "Não foi possível salvar a linha."
            } finally {
                lineBusy = false
            }
        }
    }

    fun deactivateLine(line: Line) {
        if (lineBusy) return
        lineBusy = true
        scope.launch {
            try {
                repository.deactivateLine(line.id)
                lines = repository.loadLines()
                errorMessage = null
            } catch (exception: Exception) {
                errorMessage = exception.message ?: "Não foi possível desativar a linha."
            } finally {
                lineBusy = false
            }
        }
    }

    fun submitService(
        companyId: String,
        serviceId: String?,
        draft: CompanyServiceDraft,
    ) {
        if (serviceBusy) return
        serviceBusy = true
        scope.launch {
            try {
                if (serviceId == null) {
                    repository.createService(companyId, draft)
                } else {
                    repository.updateService(serviceId, draft)
                }
                servicesByCompany = servicesByCompany - companyId
                dismissServiceForm()
                loadServices(companyId)
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível salvar o serviço."
            } finally {
                serviceBusy = false
            }
        }
    }

    fun requestDeleteService(
        companyId: String,
        serviceId: String,
    ) {
        serviceDeleteTarget = companyId to serviceId
    }

    fun dismissDeleteService() {
        serviceDeleteTarget = null
    }

    fun confirmDeleteService() {
        val target = serviceDeleteTarget ?: return
        serviceDeleteTarget = null
        if (serviceBusy) return
        serviceBusy = true
        scope.launch {
            try {
                repository.deleteService(target.second)
                servicesByCompany = servicesByCompany - target.first
                loadServices(target.first)
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível excluir o serviço."
            } finally {
                serviceBusy = false
            }
        }
    }

    private suspend fun loadServices(companyId: String) {
        loadingServicesFor = companyId
        try {
            servicesByCompany = servicesByCompany + (companyId to repository.loadServices(companyId))
        } finally {
            loadingServicesFor = null
        }
    }

    private fun loadServiceOptions() {
        scope.launch {
            serviceTypes = runCatching { repository.loadServiceTypes() }.getOrDefault(serviceTypes)
            lines = runCatching { repository.loadLines() }.getOrDefault(lines)
        }
    }

    fun goToPage(value: Int) {
        page = value.coerceIn(0, pageCount - 1)
    }

    fun openAdd() {
        formMode = CompanyFormMode.Add
        formStep = 0
        formId = null
        formName = ""
        formLegalName = ""
        formType = CompanyType.UPR
        clearAdditionalFields()
        formAdminOthersPercent = PercentFormatter.format(DEFAULT_ADMIN_OTHERS_PERCENT)
        lastLookedUpCep = ""
    }

    fun openAddCostCenter(companyId: String) {
        costCenterCompanyId = companyId
        costCenterDialog = null
        costCenterName = ""
        costCenterCode = ""
    }

    fun openEditCostCenter(center: CostCenter) {
        costCenterCompanyId = center.companyId
        costCenterDialog = center
        costCenterName = center.name
        costCenterCode = center.code
    }

    fun dismissCostCenterForm() {
        costCenterCompanyId = null
        costCenterDialog = null
    }

    fun selectCostCenterCompany(companyId: String) {
        costCenterCompanyId = companyId
    }

    fun submitCostCenter() {
        val companyId = costCenterCompanyId ?: return
        if (costCenterName.isBlank() || costCenterBusy) return
        costCenterBusy = true
        scope.launch {
            try {
                val saved =
                    costCenterDialog?.let {
                        repository.updateCostCenter(it.id, costCenterName.trim(), costCenterCode.trim())
                    } ?: repository.createCostCenter(companyId, costCenterName.trim(), costCenterCode.trim())
                costCenters = costCenters.filterNot { it.id == saved.id } + saved
                costCenters = costCenters.sortedWith(compareBy({ it.companyId }, { it.name }))
                dismissCostCenterForm()
            } catch (exception: Exception) {
                errorMessage = exception.message ?: "Não foi possível salvar o centro de custo."
            } finally {
                costCenterBusy = false
            }
        }
    }

    fun toggleCostCenter(center: CostCenter) {
        if (costCenterBusy) return
        costCenterBusy = true
        scope.launch {
            try {
                val saved = repository.changeCostCenterStatus(center, !center.active)
                costCenters = costCenters.map { if (it.id == saved.id) saved else it }
            } catch (exception: Exception) {
                errorMessage = exception.message ?: "Não foi possível alterar o status do centro de custo."
            } finally {
                costCenterBusy = false
            }
        }
    }

    fun deleteCostCenter(center: CostCenter) {
        if (costCenterBusy) return
        costCenterBusy = true
        scope.launch {
            try {
                repository.deleteCostCenter(center.id)
                costCenters = costCenters.filterNot { it.id == center.id }
            } catch (exception: Exception) {
                errorMessage = exception.message ?: "Não foi possível excluir o centro de custo."
            } finally {
                costCenterBusy = false
            }
        }
    }

    fun openEdit(company: Company) {
        formMode = CompanyFormMode.Edit
        formStep = 0
        formId = company.id
        formName = company.name
        formLegalName = company.legalName
        formType = company.type
        formLogo = company.logo
        formEmail = company.email
        formLogin = company.login
        formPassword = ""
        formCommercialPhone = formatBrazilianPhone(company.commercialPhone)
        formMobilePhone = formatBrazilianPhone(company.mobilePhone)
        formCnpj = formatBrazilianCnpj(company.cnpj)
        formEmployeeCount = company.employeeCount.formValue()
        formCep = formatBrazilianCep(company.cep)
        formStreet = company.street
        formNumber = company.number
        formNeighborhood = company.neighborhood
        formCity = company.city
        formState = company.state
        formLeftoverMeals = company.leftoverMeals.formValue()
        formLine = company.line
        formButtonCount = company.buttonCount.formValue()
        formShortAutoSendTime = company.shortAutoSendTime.formValue()
        formLongAutoSendTime = company.longAutoSendTime.formValue()
        formCommentAllRatings = company.commentAllRatings
        formAdminOthersPercent = PercentFormatter.format(company.adminOthersPercent)
        lastLookedUpCep = ""
    }

    fun dismissForm() {
        formMode = null
    }

    fun nextFormStep() {
        if (canAdvanceForm) formStep = (formStep + 1).coerceAtMost(FORM_STEP_COUNT - 1)
    }

    fun previousFormStep() {
        formStep = (formStep - 1).coerceAtLeast(0)
    }

    fun updateCommercialPhone(value: String) {
        formCommercialPhone = formatBrazilianPhone(value)
    }

    fun updateAdminOthersPercent(value: String) {
        val filtered = value.filter { it.isDigit() || it == ',' || it == '.' }
        val normalized =
            buildString {
                var separatorSeen = false
                var decimals = 0
                for (char in filtered) {
                    if (char.isDigit()) {
                        if (!separatorSeen) {
                            append(char)
                        } else if (decimals < 2) {
                            append(char)
                            decimals++
                        }
                    } else if (!separatorSeen) {
                        append(',')
                        separatorSeen = true
                    }
                }
            }
        formAdminOthersPercent = normalized
    }

    fun updateMobilePhone(value: String) {
        formMobilePhone = formatBrazilianPhone(value)
    }

    fun updateCnpj(value: String) {
        formCnpj = formatBrazilianCnpj(value)
    }

    fun updateCep(value: String) {
        formCep = formatBrazilianCep(value)
        val digits = formCep.digitsOnly()
        if (digits.length != 8) {
            lastLookedUpCep = ""
            return
        }
        if (digits == lastLookedUpCep) return
        scope.launch {
            cepLoading = true
            try {
                runCatching { cepRepository.lookup(digits) }.getOrNull()?.let { address ->
                    if (formCep.digitsOnly() == digits) {
                        lastLookedUpCep = digits
                        formStreet = address.street
                        formNeighborhood = address.neighborhood
                        formCity = address.city
                        formState = address.state
                    }
                }
            } finally {
                cepLoading = false
            }
        }
    }

    fun submitForm() {
        val name = formName.trim()
        val legalName = formLegalName.trim()
        if (name.isEmpty() || legalName.isEmpty() || hasInvalidNumericFields || busy) return
        busy = true
        scope.launch {
            try {
                val existing = companies.firstOrNull { it.id == formId }
                val company =
                    Company(
                        id = existing?.id ?: generateId(),
                        name = name,
                        legalName = legalName,
                        type = formType,
                        adminOthersPercent = PercentFormatter.parse(formAdminOthersPercent) ?: existing?.adminOthersPercent ?: DEFAULT_ADMIN_OTHERS_PERCENT,
                        logo = formLogo.trim(),
                        email = formEmail.trim(),
                        login = formLogin.trim(),
                        pendingPassword = formPassword,
                        commercialPhone = formCommercialPhone.digitsOnly(),
                        mobilePhone = formMobilePhone.digitsOnly(),
                        cnpj = formCnpj.digitsOnly(),
                        employeeCount = formEmployeeCount.toIntOrNull() ?: existing?.employeeCount ?: 0,
                        cep = formCep.digitsOnly(),
                        street = formStreet.trim(),
                        number = formNumber.trim(),
                        neighborhood = formNeighborhood.trim(),
                        city = formCity.trim(),
                        state = formState.trim(),
                        leftoverMeals = formLeftoverMeals.toIntOrNull() ?: existing?.leftoverMeals ?: 0,
                        line = formLine.trim().ifEmpty { existing?.line ?: "0" },
                        buttonCount = formButtonCount.toIntOrNull() ?: existing?.buttonCount ?: 3,
                        shortAutoSendTime = formShortAutoSendTime.toIntOrNull() ?: existing?.shortAutoSendTime ?: 2,
                        longAutoSendTime = formLongAutoSendTime.toIntOrNull() ?: existing?.longAutoSendTime ?: 45,
                        commentAllRatings = formCommentAllRatings,
                        directorNames = existing?.directorNames ?: emptyList(),
                        managerNames = existing?.managerNames ?: emptyList(),
                        updatedBy = existing?.updatedBy.orEmpty(),
                        updatedAt = existing?.updatedAt.orEmpty(),
                        inactive = existing?.inactive ?: false,
                    )
                repository.upsert(company)
                dismissForm()
                errorMessage = null
                try {
                    refresh()
                } catch (exception: Exception) {
                    errorMessage = exception.message ?: "A unidade foi salva, mas não foi possível atualizar a listagem."
                }
            } catch (exception: Exception) {
                errorMessage = exception.message ?: "Não foi possível salvar a unidade."
            } finally {
                busy = false
            }
        }
    }

    fun openDetails(company: Company) {
        detailsCompany = company
    }

    fun dismissDetails() {
        detailsCompany = null
    }

    fun requestDeactivate(company: Company) {
        deactivateTarget = company
    }

    fun dismissDeactivate() {
        deactivateTarget = null
    }

    fun confirmDeactivate() {
        val target = deactivateTarget ?: return
        scope.launch {
            try {
                repository.changeStatus(target, active = false)
                refresh()
                deactivateTarget = null
                errorMessage = null
            } catch (exception: Exception) {
                errorMessage = exception.message ?: "Não foi possível inativar a unidade."
            }
        }
    }

    fun activate(company: Company) {
        scope.launch {
            try {
                repository.changeStatus(company, active = true)
                refresh()
                errorMessage = null
            } catch (exception: Exception) {
                errorMessage = exception.message ?: "Não foi possível ativar a unidade."
            }
        }
    }

    private suspend fun refresh() {
        companies = repository.loadCompanies()
        lines = repository.loadLines()
        costCenters =
            repository
                .loadAllCostCenters()
                .sortedWith(compareBy({ it.companyId }, { it.name }))
    }

    private fun generateId(): String {
        var index = companies.size + 1
        while (companies.any { it.id == "u-novo-$index" }) {
            index++
        }
        return "u-novo-$index"
    }

    private fun clearAdditionalFields() {
        formLogo = ""
        formEmail = ""
        formLogin = ""
        formPassword = ""
        formCommercialPhone = ""
        formMobilePhone = ""
        formCnpj = ""
        formEmployeeCount = ""
        formCep = ""
        formStreet = ""
        formNumber = ""
        formNeighborhood = ""
        formCity = ""
        formState = ""
        formLeftoverMeals = ""
        formLine = "0"
        formButtonCount = ""
        formShortAutoSendTime = ""
        formLongAutoSendTime = ""
        formCommentAllRatings = false
    }

    private fun Int.formValue(): String = toString()

    private fun formatBrazilianPhone(value: String): String {
        val digits = value.digitsOnly().take(11)
        if (digits.isEmpty()) return ""
        if (digits.length <= 2) return "($digits"
        val area = digits.take(2)
        val number = digits.drop(2)
        val splitAt = if (number.length >= 8) number.length - 4 else number.length
        val separator = if (number.length >= 8) "-" else ""
        return "($area) ${number.take(splitAt)}$separator${number.drop(splitAt)}"
    }

    private fun formatBrazilianCnpj(value: String): String {
        val digits = value.digitsOnly().take(14)
        return buildString {
            digits.forEachIndexed { index, digit ->
                if (index == 2 || index == 5) append('.')
                if (index == 8) append('/')
                if (index == 12) append('-')
                append(digit)
            }
        }
    }

    private fun formatBrazilianCep(value: String): String {
        val digits = value.digitsOnly().take(8)
        return if (digits.length > 5) "${digits.take(5)}-${digits.drop(5)}" else digits
    }

    private fun String.digitsOnly(): String = filter(Char::isDigit)

    private companion object {
        const val FORM_STEP_COUNT = 3
    }
}

private object EmptyCepRepository : CepRepository {
    override suspend fun lookup(cep: String) = null
}

@Composable
fun rememberCompaniesController(): CompaniesController {
    val appGraph = LocalAppGraph.current
    val scope = rememberCoroutineScope()
    return remember {
        CompaniesController(
            repository = appGraph.companyRepository,
            scope = scope,
            dateTimeProvider = appGraph.dateTimeProvider,
            cepRepository = appGraph.cepRepository,
        )
    }
}
